import moment from "moment";
import type { LocationCapacityScheduleRow } from "../../../types/LocationCapacitySchedule";

const ISO = "YYYY-MM-DD";

/** Mirrors the API's `MAX_CAPACITY`; placeholder until stakeholders set the final limit. */
export const MAX_CAPACITY = 999_999;

export const CAPACITY_LIMIT_ERROR = `Capacity can't be more than ${MAX_CAPACITY.toLocaleString("en-US")}`;

export const todayIso = () => moment().format(ISO);

const dayBefore = (day: string) => moment(day, ISO).subtract(1, "day").format(ISO);

/**
 * The saved version of `row` when it had already started before today. Started rows are history:
 * only their end date may change. A row still running may end yesterday or later, which leaves
 * every day before today untouched.
 */
export const findStartedSavedRow = (
  row: LocationCapacityScheduleRow,
  savedRows: LocationCapacityScheduleRow[],
  today: string,
): LocationCapacityScheduleRow | undefined => {
  const saved = row.id ? savedRows.find((candidate) => candidate.id === row.id) : undefined;
  return saved && saved.effectiveDate < today ? saved : undefined;
};

const historyError = (
  row: LocationCapacityScheduleRow,
  savedRows: LocationCapacityScheduleRow[],
  today: string,
): string | undefined => {
  const started = findStartedSavedRow(row, savedRows, today);
  if (!started) {
    return row.effectiveDate < today ? "Start date can't be before today" : undefined;
  }
  if (row.effectiveDate !== started.effectiveDate || row.capacity !== started.capacity) {
    return "This row has already started, so only its end date can change";
  }
  if (row.endDate === started.endDate) {
    return undefined;
  }
  if ((started.endDate as string) < today) {
    return "This row has already ended and can't be changed";
  }
  return (row.endDate as string) < dayBefore(today) ? "End date can't be before yesterday" : undefined;
};

const formatRange = (row: LocationCapacityScheduleRow) =>
  `${moment(row.effectiveDate, ISO).format("MM/DD/YYYY")} – ${moment(row.endDate, ISO).format("MM/DD/YYYY")}`;

/**
 * Error message per row index, mirroring the API's save validation: both dates are
 * required, the end can't precede the start, capacity is at most `MAX_CAPACITY`, days
 * before today can't change (see `findStartedSavedRow`), and ranges can't share a day
 * (dates are inclusive, so a row ending on the day another starts overlaps it).
 */
export const findScheduleRowErrors = (
  rows: LocationCapacityScheduleRow[],
  savedRows: LocationCapacityScheduleRow[] = [],
  today: string = todayIso(),
): Map<number, string> => {
  const errors = new Map<number, string>();
  const complete: number[] = [];

  rows.forEach((row, index) => {
    if (!row.effectiveDate) {
      errors.set(index, "Start date is required");
    } else if (!row.endDate) {
      errors.set(index, "End date is required");
    } else if (moment(row.endDate, ISO).isBefore(moment(row.effectiveDate, ISO))) {
      errors.set(index, "End date can't be before the start date");
    } else {
      // Started rows can't be edited, so one saved above the limit before it existed mustn't block saves.
      const overLimit = row.capacity > MAX_CAPACITY && !findStartedSavedRow(row, savedRows, today);
      const rowError = overLimit ? CAPACITY_LIMIT_ERROR : historyError(row, savedRows, today);
      if (rowError) {
        errors.set(index, rowError);
      }
      complete.push(index);
    }
  });

  // ISO dates compare correctly as strings.
  const byStart = [...complete].sort((a, b) => rows[a].effectiveDate.localeCompare(rows[b].effectiveDate));
  let furthestReaching: number | null = null;
  byStart.forEach((index) => {
    const row = rows[index];
    if (furthestReaching !== null && row.effectiveDate <= (rows[furthestReaching].endDate as string)) {
      errors.set(index, `Overlaps ${formatRange(rows[furthestReaching])}`);
      if (!errors.has(furthestReaching)) {
        errors.set(furthestReaching, `Overlaps ${formatRange(row)}`);
      }
    }
    if (furthestReaching === null || (row.endDate as string) > (rows[furthestReaching].endDate as string)) {
      furthestReaching = index;
    }
  });

  return errors;
};

/**
 * Whether a started row's capacity can still be changed by splitting it: it must still cover today
 * so there are days left to give the new capacity (see {@link splitStartedRow}).
 */
export const canSplitStartedRow = (row: LocationCapacityScheduleRow, today: string = todayIso()): boolean =>
  Boolean(row.endDate && row.endDate >= today);

/**
 * Changes the capacity of a row that already started without rewriting history: the row keeps its
 * saved capacity and ends yesterday, and a new row with `capacity` runs from today to its old end date.
 */
export const splitStartedRow = (
  rows: LocationCapacityScheduleRow[],
  index: number,
  capacity: number,
  savedRows: LocationCapacityScheduleRow[],
  today: string = todayIso(),
): LocationCapacityScheduleRow[] => {
  const row = rows[index];
  const started = row ? findStartedSavedRow(row, savedRows, today) : undefined;
  if (!row || !started || !canSplitStartedRow(row, today) || capacity === started.capacity) {
    return rows;
  }
  const ended: LocationCapacityScheduleRow = { ...row, capacity: started.capacity, endDate: dayBefore(today) };
  const continuation: LocationCapacityScheduleRow = {
    id: null,
    effectiveDate: today,
    endDate: row.endDate,
    capacity,
    note: row.note,
  };
  return [...rows.slice(0, index), ended, continuation, ...rows.slice(index + 1)];
};

/** Default dates for a new row: the day after the last scheduled end date, or today if that's later. */
export const nextOpenRange = (rows: LocationCapacityScheduleRow[]): { effectiveDate: string; endDate: string } => {
  const today = todayIso();
  const lastEnd = rows.reduce<string | null>(
    (latest, row) => (row.endDate && (!latest || row.endDate > latest) ? row.endDate : latest),
    null,
  );
  const dayAfterLastEnd = lastEnd ? moment(lastEnd, ISO).add(1, "day").format(ISO) : today;
  const start = dayAfterLastEnd > today ? dayAfterLastEnd : today;
  return { effectiveDate: start, endDate: moment(start, ISO).add(1, "day").format(ISO) };
};
